import React, { useEffect, useState } from 'react';
import { generateClient } from 'aws-amplify/data';
import { parseLambdaResponse } from '../../utils/parseLambdaResponse';

const client = generateClient({ authMode: 'userPool' });

interface MyHappySnapsCardProps {
  memberSubs: string[]; // caller's own cognitoSub + any family members' cognitoSubs
}

interface Photo {
  id: string;
  photoS3Key: string;
  caption?: string | null;
  memberName?: string | null;
  status?: string | null;
  submittedAt?: string | null;
  url?: string;
}

const statusBadge = (status?: string | null) => {
  switch (status) {
    case 'WINNER':   return 'bg-yellow-100 text-yellow-800';
    case 'APPROVED': return 'bg-green-100 text-green-800';
    case 'REJECTED': return 'bg-red-100 text-red-800';
    default:         return 'bg-gray-100 text-gray-600';
  }
};

const MyHappySnapsCard: React.FC<MyHappySnapsCardProps> = ({ memberSubs }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (memberSubs.length === 0) { setLoading(false); return; }
    const load = async () => {
      try {
        // Fetch photos for every supplied memberSub (me + family). #79-class:
        // page fully — a filtered list({ limit: 50 }) caps rows SCANNED, not
        // returned, so a member's photos could be silently undercounted.
        const all: Photo[] = [];
        for (const sub of memberSubs) {
          let nextToken: string | null = null;
          do {
            const res: any = await (client.models as any).CompetitionPhoto.list({
              filter: { memberSub: { eq: sub } },
              ...(nextToken ? { nextToken } : {}),
            });
            for (const p of res?.data || []) all.push(p);
            nextToken = res?.nextToken ?? null;
          } while (nextToken);
        }
        const sorted = all
          .sort((a, b) => (b.submittedAt || '').localeCompare(a.submittedAt || ''))
          .slice(0, 12);

        // Resolve presigned download URLs
        await Promise.all(
          sorted.map(async (p) => {
            try {
              const r: any = await (client.queries as any).generateDownloadUrl({ s3Key: p.photoS3Key });
              const d = parseLambdaResponse(r?.data);
              if (d?.downloadUrl) p.url = d.downloadUrl;
            } catch { /* skip */ }
          })
        );
        setPhotos(sorted);
      } catch {
        /* silent */
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [memberSubs.join('|')]);

  if (loading) return null;
  if (photos.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">📸 My Happy Snaps</h3>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {photos.map((p) => (
          <div key={p.id} className={`relative rounded-lg overflow-hidden border ${p.status === 'WINNER' ? 'ring-2 ring-yellow-400 border-yellow-200' : 'border-gray-200'}`}>
            {p.url ? (
              <img src={p.url} alt={p.caption || 'Happy snap'} className="w-full h-28 object-cover" />
            ) : (
              <div className="w-full h-28 bg-gray-100 flex items-center justify-center text-xl text-gray-400">📷</div>
            )}
            <div className="absolute top-1 right-1">
              <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${statusBadge(p.status)}`}>
                {p.status === 'WINNER' ? '🏆 Winner' : (p.status || 'PENDING')}
              </span>
            </div>
            {p.memberName && (
              <div className="absolute bottom-1 left-1 right-1 text-[10px] text-white bg-black/50 rounded px-1 py-0.5 truncate">
                {p.memberName}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default MyHappySnapsCard;
